import { useRef, useState } from "react";
import { Type, Image as ImageIcon, Upload, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PORTAL_FONTS, loadGoogleFont, fontStack } from "@/lib/fonts";

// Self-saving per-client branding: heading/body fonts + dashboard banner + wide logo.
// Works for PR staff and the client's own client_admin (backend gates by scope).
export default function PortalBrandingControls({ clientId, branding, onSaved }) {
    const [saving, setSaving] = useState(false);

    const saveFont = async (field, value) => {
        loadGoogleFont(value);
        setSaving(true);
        try {
            const { data } = await api.patch(`/clients/${clientId}/branding`, { [field]: value });
            onSaved && onSaved(data.branding);
            toast.success("Font updated");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not save font");
        }
        setSaving(false);
    };

    return (
        <div className="mt-4 space-y-4" data-testid="portal-branding-controls">
            <div className="rounded-[10px] border p-3" style={{ borderColor: "#E5E0DA", background: "#FBFAF8" }}>
                <label className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#9A968F] mb-2">
                    <Type size={12} /> Portal &amp; PDF fonts
                </label>
                <div className="grid grid-cols-2 gap-3">
                    <FontPicker label="Heading font" value={branding?.heading_font || "Default"}
                        testId="branding-heading-font" onChange={(v) => saveFont("heading_font", v)} disabled={saving} />
                    <FontPicker label="Body font" value={branding?.body_font || "Default"}
                        testId="branding-body-font" onChange={(v) => saveFont("body_font", v)} disabled={saving} />
                </div>
                <p className="text-[11px] mt-2 text-[#6F6A64]">Applies to this client's portal screens and their branded PDF exports.</p>
            </div>

            <ImageSlot clientId={clientId} slot="banner" label="Dashboard banner"
                hint="Shown behind the portal dashboard header (a dark overlay keeps text readable). Optional."
                current={branding?.banner_url} testId="branding-banner" onSaved={onSaved} />
            <ImageSlot clientId={clientId} slot="wide-logo" label="Wide / horizontal logo"
                hint="Used in the portal header. The square logo/initials stays as the compact version."
                current={branding?.wide_logo_url} testId="branding-wide-logo" onSaved={onSaved} slotKey="wide_logo_url" />
        </div>
    );
}

function FontPicker({ label, value, onChange, testId, disabled }) {
    return (
        <div>
            <span className="text-[11px] font-semibold text-[#6F6A64] block mb-1">{label}</span>
            <select data-testid={testId} value={value} disabled={disabled}
                onChange={(e) => onChange(e.target.value)}
                className="w-full text-[13px] px-2.5 py-2 rounded-lg border bg-white disabled:opacity-60"
                style={{ borderColor: "#E5E0DA", fontFamily: fontStack(value) || undefined }}>
                {PORTAL_FONTS.map((f) => (
                    <option key={f.family} value={f.family}>{f.family}</option>
                ))}
            </select>
        </div>
    );
}

function ImageSlot({ clientId, slot, slotKey, label, hint, current, testId, onSaved }) {
    const fileRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [ver, setVer] = useState(0);
    const { user } = useAuth();
    const mode = user?.mode || "live";
    const BK = process.env.REACT_APP_BACKEND_URL;
    const key = slotKey || `${slot}_url`;
    const has = !!current;
    const previewUrl = has ? `${BK}/api/clients/${clientId}/${slot}?mode=${mode}&v=${ver}` : null;

    async function upload(file) {
        if (!file) return;
        if (!file.type.startsWith("image/")) { toast.error("Upload an image file"); return; }
        if (file.size > 8 * 1024 * 1024) { toast.error("Image must be under 8 MB"); return; }
        setBusy(true);
        try {
            const fd = new FormData(); fd.append("file", file);
            const { data } = await api.post(`/clients/${clientId}/${slot}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            onSaved && onSaved(data.branding);
            setVer((v) => v + 1);
            toast.success(`${label} uploaded`);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
        setBusy(false);
    }
    async function remove() {
        if (!window.confirm(`Remove the ${label.toLowerCase()}?`)) return;
        setBusy(true);
        try {
            const { data } = await api.delete(`/clients/${clientId}/${slot}`);
            onSaved && onSaved(data.branding);
            toast.success(`${label} removed`);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
        setBusy(false);
    }

    return (
        <div className="rounded-[10px] border p-3" style={{ borderColor: "#E5E0DA", background: "#FBFAF8" }} data-testid={`${testId}-slot`}>
            <label className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#9A968F] mb-1">
                <ImageIcon size={12} /> {label}
            </label>
            <p className="text-[11px] mb-2 text-[#6F6A64]">{hint}</p>
            {has && previewUrl && (
                <div className="mb-2.5 rounded-lg overflow-hidden border bg-[#1F2937]" style={{ borderColor: "#E5E0DA" }}>
                    <img key={ver} src={previewUrl} alt={`${label} preview`} data-testid={`${testId}-preview`}
                        className={slot === "wide-logo" ? "h-12 object-contain object-left p-2" : "w-full h-24 object-cover"}
                        onError={(e) => { e.currentTarget.style.display = "none"; }} />
                </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
                <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
                    data-testid={`${testId}-upload`}
                    className="inline-flex items-center gap-1 text-[12px] font-semibold px-3 py-1.5 rounded-lg border bg-white hover:bg-[#F5F3F0] disabled:opacity-50"
                    style={{ borderColor: "#E5E0DA", color: "#1F1F1F" }}>
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                    {has ? "Replace" : "Upload"}
                </button>
                {has && (
                    <>
                        <span className="text-[11.5px] text-[#3F7D3A] font-semibold" data-testid={`${testId}-set`}>✓ Set</span>
                        <button type="button" onClick={remove} disabled={busy} data-testid={`${testId}-remove`}
                            className="inline-flex items-center gap-1 text-[12px] font-semibold px-2.5 py-1.5 rounded-lg text-red-600 hover:bg-red-50">
                            <Trash2 size={12} /> Remove
                        </button>
                    </>
                )}
                <input ref={fileRef} type="file" accept="image/*" className="hidden"
                    onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
            </div>
        </div>
    );
}
